/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import { useViewedScreen } from "@packages/analytics";
import type { Community as CommunityType } from "@packages/sdk";
import type { CSSColorStrict, ThemeType } from "@packages/ui";
import {
  AvatarCommunity,
  radius,
  spacing,
  styleDynamics,
  Tag,
  Text,
} from "@packages/ui";

const avatarCommunityStyles: Interpolation = {
  borderRadius: radius.xl,
  flexGrow: 0,
  height: "200px",
  padding: spacing.m,
  width: "200px",
};

const groupInfoDetailsContainerStyles: Interpolation<ThemeType> = (
  theme: ThemeType,
) => ({
  alignItems: "center",
  color: theme.semanticColors.neutralsHigher,
  display: "flex",
  gap: spacing.s,
});

const groupInfoTextContainerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
};

const groupInfoDescriptionDetailsContainerStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
};

const circleStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  backgroundColor: theme.semanticColors.neutralsLow,
  borderRadius: radius.full,
  height: spacing.xxs,
  width: spacing.xxs,
});

const containerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.l,
  justifyContent: "center",
};

type CommunityProps = {
  community: CommunityType;
  screen_name?: string;
};

export const Community = ({ community, screen_name }: CommunityProps) => {
  const t = useTranslations();
  useViewedScreen({ screen: screen_name, isReady: !!screen_name });

  return (
    <div css={containerStyles}>
      {community.image_url && (
        <>
          <AvatarCommunity
            color={community.color_hex as CSSColorStrict}
            imgSrc={community.image_url}
            css={[
              avatarCommunityStyles,
              styleDynamics.displayDesktopViewport(),
            ]}
          />
          <AvatarCommunity
            size="xl"
            color={community.color_hex as CSSColorStrict}
            imgSrc={community.image_url}
            css={styleDynamics.hideDesktopViewport()}
          />
        </>
      )}
      <div css={groupInfoTextContainerStyles}>
        <Text type="title" size="xl" as="h1">
          {community.name}
        </Text>
        {community.tag_text && (
          <Tag variant="neutral">{community.tag_text}</Tag>
        )}
      </div>
      <div css={groupInfoDescriptionDetailsContainerStyles}>
        {community.description && (
          <Text type="body" size="m" as="p">
            {community.description}
          </Text>
        )}
        <span css={groupInfoDetailsContainerStyles}>
          {community.simple_address_label && (
            <Text type="detail" size="l">
              {community.simple_address_label}
            </Text>
          )}
          {community.simple_address_label &&
            community.member_summary.member_count && <div css={circleStyles} />}
          {community.member_summary.member_count > 0 && (
            <Text type="detail" size="l">
              {t("plural_members", {
                plural_members: community.member_summary.member_count,
              })}
            </Text>
          )}
        </span>
      </div>
    </div>
  );
};
